/**
 * The name under which an output table's written rows are available to the
 * tables after it (`__targets.<alias>.rowId`). Users never see it: a table
 * keeps its stored alias, otherwise one is derived from its name, unique
 * among the automation's output tables.
 */
import type { OutputTable } from '../../services/automation';

const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export function targetAliasOf(
  table: OutputTable,
  tables: readonly OutputTable[]
): string {
  if (table.targetAlias) return table.targetAlias;
  const taken = new Set<string>();
  for (const other of tables) {
    if (other === table) break;
    taken.add(targetAliasOf(other, tables));
  }
  for (const other of tables) {
    if (other.targetAlias) taken.add(other.targetAlias);
  }
  const base = slug(table.tableName) || 'table';
  let alias = base;
  for (let count = 2; taken.has(alias); count++) alias = `${base}_${count}`;
  return alias;
}

/** The alias a `__targets.<alias>.…` response field refers to, if it is one. */
export function targetAliasOfField(responseField: string): string | null {
  const match = /^__targets\.([^.]+)\./.exec(responseField);
  return match ? match[1] : null;
}

/**
 * Stores the alias of the table `responseField` refers to, so the reference
 * stays valid when tables are renamed. Returns `tables` itself when nothing
 * changes.
 */
export function persistReferencedAlias(
  tables: OutputTable[],
  responseField: string
): OutputTable[] {
  const alias = targetAliasOfField(responseField);
  if (!alias) return tables;
  const referenced = tables.find(
    table => !table.targetAlias && targetAliasOf(table, tables) === alias
  );
  if (!referenced) return tables;
  return tables.map(table =>
    table === referenced ? { ...table, targetAlias: alias } : table
  );
}
